import {
  ActionApprovalOperationType,
  MPAOperationScope,
  MPAOperationType,
  MPARequestCall,
  MPARequestStatus,
} from '@repo/api-gw-sdk';

const actionApprovalOperationsByMPAOperation: Partial<
  Record<MPAOperationType, ActionApprovalOperationType>
> = {
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_ADD_RESTORE_ACCOUNT,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_CREATE_BACKUP_POLICY]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_CREATE_BACKUP_POLICY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_RESTORE_RESOURCE]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_RESTORE_RESOURCE,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_BACKUP_POLICY]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_BACKUP_POLICY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_BACKUP_POLICY]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_DELETE_BACKUP_POLICY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_REMOVE_SNAPSHOT_HOLD]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_REMOVE_SNAPSHOT_HOLD,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_NOTIFICATION_POLICY]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_NOTIFICATION_POLICY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_NOTIFICATION_POLICY]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_DELETE_NOTIFICATION_POLICY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_INGESTION_PIPELINE]:
    ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_INGESTION_PIPELINE,
};

const mpaOperationsByActionApprovalOperation: Record<
  ActionApprovalOperationType,
  MPAOperationType
> = {
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_ADD_RESTORE_ACCOUNT]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_CREATE_BACKUP_POLICY]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_CREATE_BACKUP_POLICY,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_RESTORE_RESOURCE]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_RESTORE_RESOURCE,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_BACKUP_POLICY]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_BACKUP_POLICY,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_DELETE_BACKUP_POLICY]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_BACKUP_POLICY,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_REMOVE_SNAPSHOT_HOLD]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_REMOVE_SNAPSHOT_HOLD,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_NOTIFICATION_POLICY]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_NOTIFICATION_POLICY,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_DELETE_NOTIFICATION_POLICY]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_NOTIFICATION_POLICY,
  [ActionApprovalOperationType.ACTION_APPROVAL_OPERATION_UPDATE_INGESTION_PIPELINE]:
    MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_INGESTION_PIPELINE,
};

export const toActionApprovalOperation = (operation: MPAOperationType) =>
  actionApprovalOperationsByMPAOperation[operation];

export const toMPAOperation = (operation: ActionApprovalOperationType) =>
  mpaOperationsByActionApprovalOperation[operation];

export const operationVerbs: Record<MPAOperationType, string> = {
  [MPAOperationType.MPA_OPERATION_TYPE_UNSPECIFIED]: 'perform',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT]:
    'connect',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_CREATE_BACKUP_POLICY]:
    'create',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_BACKUP_POLICY]:
    'delete',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_BACKUP_POLICY]:
    'update',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_RESTORE_RESOURCE]:
    'restore',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_REMOVE_SNAPSHOT_HOLD]:
    'remove a snapshot hold',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_NOTIFICATION_POLICY]:
    'update',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_NOTIFICATION_POLICY]:
    'delete',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_INGESTION_PIPELINE]:
    'update',
};

const streamCallVerbs: Record<MPARequestCall, string> = {
  [MPARequestCall.MPA_REQUEST_CALL_UPDATE]: 'edit the config of',
  [MPARequestCall.MPA_REQUEST_CALL_DELETE]: 'delete',
  [MPARequestCall.MPA_REQUEST_CALL_START]: 'start',
  [MPARequestCall.MPA_REQUEST_CALL_STOP]: 'stop',
  [MPARequestCall.MPA_REQUEST_CALL_RESET_STREAM_CURSOR]:
    'reset the Kafka cursor of',
};

// Only operations that cover several calls (Update stream) are named by the call a request holds.
const callVerbsByOperation: Partial<
  Record<MPAOperationType, Record<MPARequestCall, string>>
> = {
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_INGESTION_PIPELINE]:
    streamCallVerbs,
};

export const requestVerb = (
  call: MPARequestCall | undefined,
  operation: MPAOperationType | undefined
) =>
  (call && operation && callVerbsByOperation[operation]?.[call]) ??
  (operation && operationVerbs[operation]) ??
  'perform';

export const noScopeAllCopy: Partial<Record<MPAOperationType, string>> = {
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT]:
    'All restore accounts',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_CREATE_BACKUP_POLICY]:
    'All new backup policies',
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_NOTIFICATION_POLICY]:
    'All notification policies',
};

interface ScopeCopy {
  allCopy: string;
  selectionCopy: string;
}

export const scopeCopy: Partial<Record<MPAOperationScope, ScopeCopy>> = {
  [MPAOperationScope.MPA_OPERATION_SCOPE_BACKUP_POLICY]: {
    allCopy: 'All current and future backup policies',
    selectionCopy: 'These backup policies:',
  },
  [MPAOperationScope.MPA_OPERATION_SCOPE_CLOUD_RESOURCE]: {
    allCopy: 'All current and future resources',
    selectionCopy: 'These resources:',
  },
  [MPAOperationScope.MPA_OPERATION_SCOPE_NOTIFICATION_POLICY]: {
    allCopy: 'All current and future notification policies',
    selectionCopy: 'These notification policies:',
  },
  [MPAOperationScope.MPA_OPERATION_SCOPE_INGESTION_PIPELINE]: {
    allCopy: 'All current and future streams',
    selectionCopy: 'These streams:',
  },
};

type StatusTone =
  'pending' | 'success' | 'error' | 'neutral' | 'disabled' | 'apply';

interface StatusDescriptor {
  label: string;
  tone: StatusTone;
}

export const statusDescriptors: Record<MPARequestStatus, StatusDescriptor> = {
  [MPARequestStatus.MPA_REQUEST_STATUS_UNSPECIFIED]: {
    label: 'Unknown',
    tone: 'neutral',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_CREATED]: {
    label: 'Created',
    tone: 'neutral',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_PENDING_APPROVAL]: {
    label: 'Pending approval',
    tone: 'pending',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_APPROVED]: {
    label: 'Ready to apply',
    tone: 'apply',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_DENIED]: {
    label: 'Denied',
    tone: 'error',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_EXPIRED_PENDING_APPROVAL]: {
    label: 'Expired',
    tone: 'disabled',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_EXPIRED_APPROVED]: {
    label: 'Expired',
    tone: 'disabled',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_EXECUTED]: {
    label: 'Completed',
    tone: 'success',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_CANCELLED_PENDING_APPROVAL]: {
    label: 'Canceled',
    tone: 'disabled',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_CANCELLED_APPROVED]: {
    label: 'Canceled',
    tone: 'disabled',
  },
  [MPARequestStatus.MPA_REQUEST_STATUS_DISCARDED]: {
    label: 'Discarded',
    tone: 'disabled',
  },
};

export const toneColors: Record<StatusTone, { bg: string; fg: string }> = {
  pending: {
    bg: 'var(--mui-palette-warning-bgLight)',
    fg: 'var(--mui-palette-warning-main)',
  },
  success: {
    bg: 'var(--mui-palette-success-bgLight)',
    fg: 'var(--mui-palette-success-main)',
  },
  error: {
    bg: 'var(--mui-palette-error-opacity)',
    fg: 'var(--mui-palette-error-main)',
  },
  neutral: {
    bg: 'var(--mui-palette-background-default)',
    fg: 'var(--mui-palette-text-secondary)',
  },
  disabled: {
    bg: 'var(--mui-palette-background-default)',
    fg: 'var(--mui-palette-text-disabled)',
  },
  apply: {
    bg: 'var(--mui-palette-custom-ORANGE-OPACITY-01)',
    fg: 'var(--mui-palette-custom-ORANGE-01)',
  },
};
